import { h } from 'preact';

import { SCENE_CSS } from './keyframes';
import {
    layerTransform, DEPTH_SKY, DEPTH_FAR, DEPTH_MID, DEPTH_GROUND, DEPTH_HERO,
} from './parallax';
import { Cloud, GroundBand } from './sceneProps';

// Long-distance mushing: a dog team crossing the vidda. Light theme is a
// winter day (low sun), dark theme is a night trail (northern lights, stars
// and the musher's headlamp). The team is drawn in its own fixed-pixel SVG
// (like the icon heroes in the other scenes) so it keeps its size on every
// screen width.

const STARS = [
    [40, 18, 1.2], [95, 34, 0.9], [150, 10, 1.1], [215, 26, 0.8], [268, 14, 1.3],
    [330, 40, 0.9], [392, 8, 1], [455, 22, 1.2], [512, 12, 0.8], [575, 36, 1],
    [630, 16, 1.3], [690, 30, 0.9], [742, 10, 1.1], [780, 38, 0.8],
];

// Snow caps on the three tallest peaks of the far ridge.
const SNOW_CAPS = [
    'M190 58 L173 64.5 L180 63 L186 67 L193 63 L199 66 L206 64 Z',
    'M350 72 L336 74.8 L342 74.5 L347 77 L353 74.5 L358 77 L364 75.9 Z',
    'M560 64 L543 69.6 L550 68 L556 72 L563 68 L569 71 L576 69 Z',
];

// Snow-laden spruce: three stacked tiers, a trunk and a snowy tip.
const Spruce = ({ x, y = 84, s = 1, palette }) => (
    <g>
        <rect x={x - 2 * s} y={y + 34 * s} width={4 * s} height={10 * s + 6} fill={palette.mid} />
        <polygon points={`${x},${y + 17 * s} ${x - 13 * s},${y + 36 * s} ${x + 13 * s},${y + 36 * s}`} fill={palette.mid} />
        <polygon points={`${x},${y + 8 * s} ${x - 10 * s},${y + 24 * s} ${x + 10 * s},${y + 24 * s}`} fill={palette.mid} />
        <polygon points={`${x},${y} ${x - 7 * s},${y + 14 * s} ${x + 7 * s},${y + 14 * s}`} fill={palette.mid} />
        <polygon points={`${x},${y} ${x - 3.5 * s},${y + 7 * s} ${x + 3.5 * s},${y + 7 * s}`} fill={palette.sky} opacity='0.8' />
    </g>
);

// Trail marker stake (merkestikke) with a reflector top.
const TrailMarker = ({ x, palette }) => (
    <g>
        <rect x={x - 1.25} y='100' width='2.5' height='30' fill={palette.mid} />
        <rect x={x - 1.25} y='100' width='2.5' height='6' fill={palette.accent} opacity='0.9' />
    </g>
);

// Seconds as a negative CSS delay, so each leg/dog starts mid-cycle.
const phaseDelay = (stride, phase) => (-(stride * (phase % 1))).toFixed(2);

// Diagonal leg pairs move together (near front + far hind) — a trot.
const LEGS = [
    { x: 6, phase: 0 },
    { x: 3, phase: 0.5 },
    { x: -6, phase: 0.5 },
    { x: -9, phase: 0 },
];

// One sled dog facing right; (x, y) is the body centre, paws land at y + 12.
const Dog = ({
    x, y, fill, opacity = 1, stride, offset = 0,
}) => (
    <g class='dog' transform={`translate(${x} ${y})`} fill={fill} opacity={opacity}>
        <g style={`animation: refreshDogBob ${stride}s ease-in-out ${phaseDelay(stride, offset)}s infinite;`}>
            {LEGS.map((leg) => (
                <rect
                    key={leg.x}
                    class='dog-leg'
                    x={leg.x - 1.2}
                    y='1'
                    width='2.4'
                    height='11'
                    rx='1.2'
                    style={`animation: refreshDogStride ${stride}s ease-in-out ${phaseDelay(stride, offset + leg.phase)}s infinite;`}
                />
            ))}
            <ellipse cx='0' cy='0' rx='11' ry='5' />
            <polygon points='6,-3 11,-10 15,-9 11,2' />
            <ellipse cx='15' cy='-9' rx='4.5' ry='3.5' />
            <polygon points='18,-11 23,-8.5 18,-6.5' />
            <polygon points='11.5,-11 13,-16 15.5,-11.5' />
            <path d='M-10 -2 Q -17 -10 -11 -13 Q -8 -9 -8 -3 Z' />
        </g>
    </g>
);

// Three pairs: wheel dogs, team dogs, leaders. The far-side dog of each pair
// is offset up/right and dimmed so the pair reads as two dogs side by side.
const DOG_PAIRS = [96, 158, 220];

const SNOW_PUFFS = [
    { x: 6, y: 55, r: 3, delay: 0 },
    { x: 12, y: 54, r: 2.2, delay: 0.05 },
    { x: 3, y: 53, r: 2, delay: 0.1 },
    { x: 9, y: 51, r: 1.8, delay: 0.14 },
    { x: 15, y: 56, r: 2.4, delay: 0.08 },
];

const Mushing = ({
    progress = 0, pastThreshold = false, released = false,
    pullOffset = 0, palette = {}, dark = false,
}) => {
    const driftSpeed = (26 - 14 * progress).toFixed(2);
    // Seconds per stride — the team picks up the pace as you pull.
    const stride = Number((0.6 - 0.32 * progress).toFixed(2));
    const auroraAnimation = released
        ? 'animation: refreshAuroraFlare 0.6s ease-out forwards;'
        : 'animation: refreshAuroraShimmer 7s ease-in-out infinite;';

    return (
        <div style={`position: relative; width: 100%; height: 100%; ${pastThreshold ? 'filter: brightness(1.1);' : ''}`}>
            <svg
                viewBox='0 0 800 150'
                preserveAspectRatio='xMidYMax slice'
                role='img'
                class='refresh-scene'
                style='position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible;'
            >
                <style>{SCENE_CSS}</style>
                <title>Dra ned for å oppdatere</title>

                {/* Sky — night: stars + northern lights; day: low winter sun + clouds */}
                <g style={layerTransform(pullOffset, DEPTH_SKY)}>
                    {dark ? (
                        <g>
                            {STARS.map(([x, y, r], idx) => (
                                <circle key={x} cx={x} cy={y} r={r} fill={palette.sky} opacity={idx % 2 === 0 ? 0.9 : 0.55} />
                            ))}
                            <g
                                class='mushing-aurora'
                                fill='none'
                                stroke={palette.glow}
                                stroke-linecap='round'
                                style={`${auroraAnimation} transform-box: fill-box; transform-origin: 50% 100%;`}
                            >
                                <path d='M-60 56 C 80 26, 200 70, 360 42 S 620 18, 860 48' stroke-width='14' opacity='0.22' />
                                <path d='M-60 52 C 80 24, 200 66, 360 39 S 620 16, 860 44' stroke-width='5' opacity='0.5' />
                                <path d='M-40 70 C 120 48, 260 82, 420 60 S 660 40, 860 64' stroke-width='8' opacity='0.16' />
                            </g>
                        </g>
                    ) : (
                        <g>
                            <g class='mushing-sun' fill={palette.glow}>
                                <circle cx='480' cy='58' r='24' opacity='0.3' />
                                <circle cx='480' cy='58' r='14' />
                            </g>
                            <Cloud x={120} y={24} fill={palette.sky} />
                            <Cloud x={640} y={18} s={0.8} fill={palette.sky} />
                        </g>
                    )}
                </g>

                {/* Far — mountain ridge with snow caps */}
                <g style={layerTransform(pullOffset, DEPTH_FAR)}>
                    <path d='M-100 150 L-100 100 L 20 80 L 90 96 L 190 58 L 270 88 L 350 72 L 450 100 L 560 64 L 650 92 L 740 76 L 900 98 L 900 150 Z' fill={palette.far} />
                    {SNOW_CAPS.map((d) => (
                        <path key={d} d={d} fill={palette.sky} opacity='0.85' />
                    ))}
                </g>

                {/* Mid — spruce and trail markers, drifting by */}
                <g style={layerTransform(pullOffset, DEPTH_MID)}>
                    <g class='drift' style={`animation-duration: ${driftSpeed}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                <Spruce x={40} palette={palette} />
                                <Spruce x={70} y={95} s={0.75} palette={palette} />
                                <TrailMarker x={140} palette={palette} />
                                <Spruce x={210} palette={palette} />
                                <Spruce x={248} y={91} s={0.8} palette={palette} />
                                <TrailMarker x={330} palette={palette} />
                                <Spruce x={420} y={80} s={1.1} palette={palette} />
                                <Spruce x={456} y={98} s={0.7} palette={palette} />
                                <TrailMarker x={530} palette={palette} />
                                <Spruce x={600} palette={palette} />
                                <TrailMarker x={660} palette={palette} />
                                <Spruce x={705} y={91} s={0.85} palette={palette} />
                                <Spruce x={745} y={98} s={0.7} palette={palette} />
                            </g>
                        ))}
                    </g>
                </g>

                {/* Ground — snow with drifting sled-runner tracks */}
                <g style={layerTransform(pullOffset, DEPTH_GROUND)}>
                    <GroundBand fill={palette.ground} />
                    <g class='drift' style={`animation-duration: ${(driftSpeed / 2.5).toFixed(2)}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                {[0, 80, 160, 240, 320, 400, 480, 560, 640, 720].map((x) => (
                                    <g key={x} stroke={palette.text} stroke-width='1.5' opacity='0.2'>
                                        <line x1={x} y1='134' x2={x + 56} y2='134' />
                                        <line x1={x + 6} y1='139' x2={x + 62} y2='139' />
                                    </g>
                                ))}
                            </g>
                        ))}
                    </g>
                </g>
            </svg>

            {/* Hero — musher, sled and a six-dog team heading right */}
            <div style={`position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; pointer-events: none; ${layerTransform(pullOffset, DEPTH_HERO)}`}>
                <svg
                    class='mushing-team'
                    viewBox='0 0 250 60'
                    width='250'
                    height='60'
                    aria-hidden='true'
                    style='overflow: visible; margin-bottom: 22px;'
                >
                    {/* Headlamp beam lighting the trail ahead, fading out (night only) */}
                    {dark && (
                        <g class='mushing-headlamp'>
                            <defs>
                                <linearGradient id='refreshMushingBeam' x1='0' y1='0' x2='1' y2='0'>
                                    <stop offset='0' stop-color={palette.sky} stop-opacity='0.3' />
                                    <stop offset='1' stop-color={palette.sky} stop-opacity='0' />
                                </linearGradient>
                            </defs>
                            <polygon points='26,19 262,30 262,58 26,22' fill='url(#refreshMushingBeam)' />
                            <circle cx='26.2' cy='19.6' r='1.6' fill={palette.sky} />
                        </g>
                    )}

                    {/* Far-side dogs, then the gangline, then near-side dogs */}
                    {DOG_PAIRS.map((x, idx) => (
                        <Dog key={`far-${x}`} x={x + 6} y={43} fill={palette.text} opacity={0.55} stride={stride} offset={idx * 0.23 + 0.11} />
                    ))}
                    <path d='M66 49 L84 44 L236 42' fill='none' stroke={palette.text} stroke-width='1.2' opacity='0.8' />
                    {DOG_PAIRS.map((x, idx) => (
                        <Dog key={`near-${x}`} x={x} y={46} fill={palette.text} stride={stride} offset={idx * 0.23} />
                    ))}

                    {/* Sled + musher on the runner tails */}
                    <g fill={palette.text} stroke={palette.text} stroke-linecap='round' stroke-linejoin='round'>
                        <path d='M4 57 L60 57 Q68 57 68 49' fill='none' stroke-width='2.2' />
                        <path d='M31 56 L31 47 M45 56 L45 47 M59 56 L59 48' fill='none' stroke-width='1.8' />
                        <rect x='29' y='44' width='33' height='4' rx='1.5' stroke='none' />
                        <rect x='33' y='36' width='27' height='9' rx='3.5' stroke='none' />
                        <path d='M31 46 L29 30 Q29 27 32 27' fill='none' stroke-width='2' />
                        <path d='M12 56 L16 42 M20 56 L19 42' fill='none' stroke-width='3.4' />
                        <path d='M12 44 L22 44 L27 29 L19 26 Z' stroke='none' />
                        <path d='M24 31 L31 29' fill='none' stroke-width='2.8' />
                        <circle cx='22' cy='21' r='4.6' stroke='none' />
                        <circle cx='21' cy='15.4' r='1.7' stroke='none' />
                    </g>

                    {/* Release — checkpoint sign slides past, snow sprays off the runners */}
                    {released && (
                        <g>
                            <g transform='translate(262 0)'>
                                <g class='mushing-checkpoint' style='animation: refreshCheckpointPass 0.6s ease-out forwards;'>
                                    <rect x='-1.5' y='14' width='3' height='44' rx='1' fill={palette.text} />
                                    <polygon points='1.5,14 16,18.5 1.5,23' fill={palette.accent} />
                                    <rect x='-13' y='27' width='26' height='13' rx='2.5' fill={palette.accent} />
                                    <text x='0' y='36.8' text-anchor='middle' font-size='9' font-weight='700' fill='#ffffff'>CP</text>
                                </g>
                            </g>
                            <g class='mushing-snow-spray' fill={palette.sky}>
                                {SNOW_PUFFS.map(({
                                    x, y, r, delay,
                                }) => (
                                    <circle
                                        key={`${x}-${y}`}
                                        class='snow-puff'
                                        cx={x}
                                        cy={y}
                                        r={r}
                                        style={`animation: refreshSnowSpray 0.6s ease-out ${delay}s forwards; opacity: 0;`}
                                    />
                                ))}
                            </g>
                        </g>
                    )}
                </svg>
            </div>
        </div>
    );
};

export default Mushing;
